import { useEffect, useRef, useState } from "react";
import { Form } from "@/components/shared/forms/Form";
import { useForm, SubmitHandler } from "react-hook-form";
import { useRouteContext } from "@tanstack/react-router";

import { ENRLAddEditForm } from "@/components/records/child/ENRLAddEditForm";
import Popup from "@/components/shared/Popup";
import LoaderButton from "@/components/shared/LoaderButton";
import { DeleteConfirmModal } from "@/components/shared/DeleteConfirmModal";
import { useRecordSubmission } from "@/hooks/useRecordSubmission";
import { useCoreData, useLeaData } from "@/queries.ts";
import { ENRL_TEMPLATE, type ENRL } from "@/models";
import { enrlMutationFunctions } from "@/models/chld";
import { normalizeCodeSetValue } from "@/utils/util";
import {
  FiArrowLeft,
  FiEdit,
  FiEye,
  FiPlus,
  FiSave,
  FiTrash2,
} from "react-icons/fi";

export type ENRLEditDialogProps = {
  selectedEnrollment: ENRL | undefined;
  enrollmentDefaults?: Partial<ENRL>;
  onClose: () => void;
  viewOnly?: boolean;
  onDeleted?: (record: ENRL) => void;
  footerMode?: "default" | "back";
};

export default function ENRLEditDialog({
  selectedEnrollment,
  enrollmentDefaults,
  onClose,
  viewOnly = false,
  onDeleted,
  footerMode = "default",
}: ENRLEditDialogProps) {
  const leaContext = useRouteContext({ from: "/$leaid" });
  const leaData = useLeaData(leaContext.currentUserOrg.esiIdPath).data;
  const coreData = useCoreData();
  // Read via a ref inside the reset effect below so it isn't a reactive dependency:
  // coreData is only used to normalize code casing, not to decide when to re-hydrate the form.
  const coreDataRef = useRef(coreData);
  coreDataRef.current = coreData;

  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const initialValues: ENRL =
    selectedEnrollment ??
    ({ ...ENRL_TEMPLATE, ...(enrollmentDefaults ?? {}) } as ENRL);

  const addEditForm = useForm<ENRL>({ defaultValues: initialValues });

  useEffect(() => {
    if (selectedEnrollment) {
      addEditForm.reset({
        ...selectedEnrollment,
        ProgramType: normalizeCodeSetValue(
          selectedEnrollment.ProgramType,
          coreDataRef.current.programTypeList
        ),
        ProgramDuration: normalizeCodeSetValue(
          selectedEnrollment.ProgramDuration,
          coreDataRef.current.programDurationList
        ),
        ContractType: normalizeCodeSetValue(
          selectedEnrollment.ContractType,
          coreDataRef.current.contractTypesOfferedList
        ),
        EligibilityStatus: normalizeCodeSetValue(
          selectedEnrollment.EligibilityStatus,
          coreDataRef.current.eligibilityStatusList
        ),
        EnrollmentEndReason: normalizeCodeSetValue(
          selectedEnrollment.EnrollmentEndReason,
          coreDataRef.current.studentExitCategoryList
        ),
      });
    } else {
      addEditForm.reset({ ...ENRL_TEMPLATE, ...(enrollmentDefaults ?? {}) });
    }
    addEditForm.clearErrors();
  }, [selectedEnrollment, enrollmentDefaults, addEditForm]);

  const { isSubmitting, setSubmitResult, handleSubmitStart } =
    useRecordSubmission({
      form: addEditForm,
      recordType: "ENRL",
      toastMessage: "Enrollment record submitted.\nWaiting for validation...",
      onSuccess: onClose,
    });

  const {
    isSubmitting: isDeleteSubmitting,
    setSubmitResult: setDeleteSubmitResult,
    handleSubmitStart: handleDeleteStart,
  } = useRecordSubmission({
    form: addEditForm,
    recordType: "ENRL",
    toastMessage: "Enrollment record submitted for deletion",
    successMessage: "Enrollment record deleted successfully",
    onSuccess: () => {
      if (selectedEnrollment) onDeleted?.(selectedEnrollment);
      onClose();
    },
  });

  const mutateFns = enrlMutationFunctions(leaData, setSubmitResult);
  const deleteMutateFns = enrlMutationFunctions(leaData, setDeleteSubmitResult);

  const onSubmit: SubmitHandler<ENRL> = (x) => {
    handleSubmitStart();
    mutateFns.handleSave(x);
  };

  const handleConfirmDelete = () => {
    setShowDeleteConfirm(false);
    if (selectedEnrollment) {
      setIsDeleting(true);
      handleDeleteStart();
      deleteMutateFns.handleSave(selectedEnrollment, true);
    }
  };

  const formId = "frmENRL";
  const hasRole = leaContext.hasRole("ENRLEdit");
  const canEdit = hasRole && !viewOnly;
  const isBack = footerMode === "back";

  const deleteButton =
    hasRole && selectedEnrollment ? (
      <LoaderButton
        variant="danger"
        type="button"
        onClick={() => setShowDeleteConfirm(true)}
        loading={isDeleting && isDeleteSubmitting}
        loadingText="Deleting..."
        className="d-flex align-items-center"
      >
        <FiTrash2 className="me-1" />
        Delete
      </LoaderButton>
    ) : undefined;

  return (
    <>
      <Popup
        show={true}
        enforceFocus={!showDeleteConfirm}
        header={
          selectedEnrollment == null ? (
            <div
              className="d-flex align-items-center gap-2"
              style={{ fontSize: "1.25rem" }}
            >
              {canEdit ? <FiPlus size={20} /> : <FiEye size={20} />}
              <span>
                {canEdit ? "Add Enrollment Record" : "View Enrollment Record"}
              </span>
            </div>
          ) : (
            <div
              className="d-flex align-items-center gap-2"
              style={{ fontSize: "1.25rem" }}
            >
              {canEdit ? <FiEdit size={20} /> : <FiEye size={20} />}
              <span>
                {canEdit ? "Edit Enrollment Record" : "View Enrollment Record"}
              </span>
            </div>
          )
        }
        hasButton={false}
        submitFormId={canEdit ? formId : undefined}
        submitButtonText={
          selectedEnrollment ? (
            <span className="d-flex align-items-center">
              <FiEdit className="me-1" />
              Update
            </span>
          ) : (
            <span className="d-flex align-items-center">
              <FiSave className="me-1" />
              Save
            </span>
          )
        }
        submitButtonLoading={isSubmitting}
        submitButtonLoadingText="Submitting..."
        secondButtonText={isBack ? undefined : "Cancel"}
        size="lg"
        onClose={onClose}
        onSecondButtonClick={isBack ? undefined : onClose}
        backButtonText={
          isBack ? (
            <div className="d-flex align-items-center gap-2">
              <FiArrowLeft size={16} />
              <span>Back</span>
            </div>
          ) : undefined
        }
        onBackButtonClick={isBack ? onClose : undefined}
        canEdit={canEdit}
        additionalFooterButton={isBack ? deleteButton : undefined}
        leftFooterButton={isBack ? undefined : deleteButton}
      >
        <Form id={formId} onSubmit={addEditForm.handleSubmit(onSubmit)}>
          <ENRLAddEditForm
            selectedEnrollment={selectedEnrollment}
            form={addEditForm}
            enrollmentDefaults={enrollmentDefaults}
          />
        </Form>
      </Popup>

      {showDeleteConfirm && (
        <DeleteConfirmModal
          recordKindLabel="Enrollment record"
          onCancel={() => setShowDeleteConfirm(false)}
          onConfirm={handleConfirmDelete}
        />
      )}
    </>
  );
}
